import React, { useState, useEffect } from 'react';
import api from '../utils/api';
import { toast } from 'react-toastify';
import './PrivacySettings.css';

const PrivacySettings = ({ userId }) => {
  const [settings, setSettings] = useState({
    profileVisibility: 'public',
    showEmail: true,
    showPhone: false
  });
  const [loading, setLoading] = useState(false);
  const [profileUrl, setProfileUrl] = useState('');

  useEffect(() => {
    if (userId) {
      fetchPrivacySettings();
      const url = `${window.location.origin}/public-profile/${userId}`;
      setProfileUrl(url);
    }
  }, [userId]);

  const fetchPrivacySettings = async () => {
    if (!userId) return;
    
    try {
      const response = await api.get('/users/profile');

      if (response.data.success && response.data.user.privacy) {
        setSettings(response.data.user.privacy);
      }
    } catch (error) {
      console.error('Error fetching privacy settings:', error);
    }
  };

  const handleSave = async () => {
    try {
      setLoading(true);
      
      const response = await api.put('/public-profile/settings', settings);

      if (response.data.success) {
        toast.success('Privacy settings updated successfully!');
      }
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to update privacy settings');
    } finally {
      setLoading(false);
    }
  };

  const copyProfileLink = () => {
    navigator.clipboard.writeText(profileUrl);
    toast.success('Profile link copied to clipboard!');
  };

  const shareOnWhatsApp = () => {
    const text = 'Check out my profile on Cixio';
    window.open(`https://wa.me/?text=${encodeURIComponent(text + ' ' + profileUrl)}`, '_blank');
  };

  const shareOnTwitter = () => {
    const text = 'Check out my profile on Cixio';
    window.open(`https://twitter.com/intent/tweet?text=${encodeURIComponent(text)}&url=${encodeURIComponent(profileUrl)}`, '_blank');
  };

  const shareOnLinkedIn = () => {
    window.open(`https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(profileUrl)}`, '_blank');
  };

  if (!userId) {
    return (
      <div className="privacy-settings-container">
        <div className="error-message">
          <p>⚠️ User information is loading...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="privacy-settings-container">
      <h2>🔒 Privacy & Public Profile Settings</h2>
      
      <div className="privacy-section">
        <h3>Public Profile Link</h3>
        <p className="section-description">
          Share your public profile with others to showcase your credibility and reviews
        </p>
        
        <div className="profile-link-box">
          <input 
            type="text" 
            value={profileUrl} 
            readOnly 
            className="profile-link-input"
          />
          <button onClick={copyProfileLink} className="btn-copy" title="Copy Link">
            📋 Copy
          </button>
        </div>

        <div className="share-buttons">
          <button onClick={shareOnWhatsApp} className="btn-share whatsapp">
            <span>📱</span> WhatsApp
          </button>
          <button onClick={shareOnTwitter} className="btn-share twitter">
            <span>🐦</span> Twitter
          </button>
          <button onClick={shareOnLinkedIn} className="btn-share linkedin">
            <span>💼</span> LinkedIn
          </button>
          <a 
            href={profileUrl} 
            target="_blank" 
            rel="noopener noreferrer" 
            className="btn-share preview"
          >
            <span>👁️</span> Preview
          </a>
        </div>
      </div>

      <div className="privacy-section">
        <h3>Profile Visibility</h3>
        <p className="section-description">
          Control who can view your public profile
        </p>

        <div className="radio-group">
          <label className={`radio-option ${settings.profileVisibility === 'public' ? 'selected' : ''}`}>
            <input
              type="radio"
              name="profileVisibility"
              value="public"
              checked={settings.profileVisibility === 'public'}
              onChange={(e) => setSettings({ ...settings, profileVisibility: e.target.value })}
            />
            <div className="radio-content">
              <strong>🌍 Public</strong>
              <span>Anyone can view your profile, even without logging in</span>
            </div>
          </label>

          <label className={`radio-option ${settings.profileVisibility === 'connections' ? 'selected' : ''}`}>
            <input
              type="radio"
              name="profileVisibility"
              value="connections"
              checked={settings.profileVisibility === 'connections'}
              onChange={(e) => setSettings({ ...settings, profileVisibility: e.target.value })}
            />
            <div className="radio-content">
              <strong>👥 Connections Only</strong>
              <span>Only users you've interacted with can view your profile</span>
            </div>
          </label>

          <label className={`radio-option ${settings.profileVisibility === 'private' ? 'selected' : ''}`}>
            <input
              type="radio"
              name="profileVisibility"
              value="private"
              checked={settings.profileVisibility === 'private'}
              onChange={(e) => setSettings({ ...settings, profileVisibility: e.target.value })}
            />
            <div className="radio-content">
              <strong>🔒 Private</strong>
              <span>Your profile is hidden from public view</span>
            </div>
          </label>
        </div>
      </div>

      <div className="privacy-section">
        <h3>Contact Information</h3>
        <p className="section-description">
          Choose what contact information to display on your public profile
        </p>

        <div className="checkbox-group">
          <label className="checkbox-option">
            <input
              type="checkbox"
              checked={settings.showEmail}
              onChange={(e) => setSettings({ ...settings, showEmail: e.target.checked })}
            />
            <div className="checkbox-content">
              <strong>📧 Show Email Address</strong>
              <span>Display your email address on your public profile</span>
            </div>
          </label>

          <label className="checkbox-option">
            <input
              type="checkbox"
              checked={settings.showPhone}
              onChange={(e) => setSettings({ ...settings, showPhone: e.target.checked })}
            />
            <div className="checkbox-content">
              <strong>📱 Show Phone Number</strong>
              <span>Display your phone number on your public profile</span>
            </div>
          </label>
        </div>
      </div>

      <div className="privacy-actions">
        <button 
          onClick={handleSave} 
          disabled={loading}
          className="btn-save"
        >
          {loading ? 'Saving...' : '💾 Save Privacy Settings'}
        </button>
      </div>

      <div className="privacy-info-box">
        <h4>ℹ️ Why share your public profile?</h4>
        <ul>
          <li>Build trust with potential customers</li>
          <li>Showcase your ratings and reviews</li>
          <li>Display your verified credentials</li>
          <li>Share on social media and business cards</li>
          <li>Increase your visibility in the marketplace</li>
        </ul>
      </div>
    </div>
  );
};

export default PrivacySettings;
